import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box, Stack, Typography, Paper, Chip, Collapse, LinearProgress,
  FormControlLabel, Switch, IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import RestaurantMenuRoundedIcon from '@mui/icons-material/RestaurantMenuRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';

import { surface, brand, ink, line } from '../../theme/tokens';
import { dayProgress } from '../../utils/farmStayBooking';

/**
 * Choosing the food for a stay.
 *
 * This replaced a flat list of `<TextField select>` dropdowns. On a three-night
 * stay that was fourteen dropdowns in one scroll, every option hidden behind a
 * tap, no sense of how much was left and no sign of what else came with a meal.
 *
 * Three things do the work:
 *   - one day open at a time, so the page is short whatever the stay length;
 *   - options as tappable cards, so the description is readable without
 *     opening a menu and the target is a thumb rather than a line of text;
 *   - a count that is always on screen, plus a day rail to jump around.
 *
 * Built for a phone first: the option grid is a single column until there is
 * room for two, every target clears 48px, and nothing depends on hover.
 */

/** A day is a "question" container; this is how many of them are answered. */
const DAY_RAIL_LABEL = (day) =>
  (day.date
    ? new Date(`${day.date}T00:00:00Z`).toLocaleDateString('en-IN', {
      weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
    })
    : `Day ${day.stayDayIndex}`);

const OptionCard = ({ option, selected, onSelect }) => (
  <Box
    component="button"
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onSelect}
    sx={{
      textAlign: 'left',
      font: 'inherit',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.25,
      // a thumb, not a line of text
      minHeight: 52,
      p: 1.5,
      borderRadius: 2,
      border: '1.5px solid',
      borderColor: selected ? brand.mustardInk : line.default,
      bgcolor: selected ? alpha(brand.mustard, 0.14) : surface.card,
      transition: 'border-color 140ms ease, background-color 140ms ease',
      '&:hover': { borderColor: selected ? brand.mustardInk : line.strong },
      '&:focus-visible': { outline: `2px solid ${brand.mustardInk}`, outlineOffset: 2 },
    }}
  >
    <Box
      aria-hidden
      sx={{
        flexShrink: 0,
        mt: '2px',
        width: 20,
        height: 20,
        borderRadius: '50%',
        border: '1.5px solid',
        borderColor: selected ? brand.mustardInk : line.strong,
        bgcolor: selected ? brand.mustardInk : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {selected && <CheckRoundedIcon sx={{ fontSize: 14, color: surface.card }} />}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{ fontWeight: selected ? 700 : 600, color: ink.primary, fontSize: 15, lineHeight: 1.35 }}
      >
        {option.label}
      </Typography>
      {option.description && (
        <Typography sx={{ color: ink.secondary, fontSize: 13, lineHeight: 1.5, mt: 0.25 }}>
          {option.description}
        </Typography>
      )}
    </Box>
  </Box>
);

const MealCard = ({ meal, choices, onChoose }) => {
  const answered = meal.groups.filter((g) => choices[g.key]).length;
  const complete = meal.groups.length > 0 && answered === meal.groups.length;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.75, sm: 2.25 },
        borderRadius: 2.5,
        border: `1px solid ${complete ? alpha(brand.oliveInk, 0.35) : line.subtle}`,
        bgcolor: surface.card,
      }}
    >
      <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap>
        {meal.timeLabel && (
          <Typography
            sx={{
              fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', color: brand.mustardInk,
              bgcolor: alpha(brand.mustard, 0.16), px: 0.85, py: 0.25, borderRadius: 1,
            }}
          >
            {meal.timeLabel}
          </Typography>
        )}
        <Typography sx={{ fontWeight: 700, color: ink.primary, fontSize: 16 }}>
          {meal.title}
        </Typography>
        {complete && (
          <CheckRoundedIcon sx={{ fontSize: 17, color: brand.oliveInk, ml: 'auto' }} aria-label="chosen" />
        )}
      </Stack>

      {/* What arrives whatever you pick. Without this the choice reads as the
          whole meal, and guests think a weekend is three dishes. */}
      {meal.fixedItems?.length > 0 && (
        <Typography sx={{ color: ink.secondary, fontSize: 13, lineHeight: 1.55, mt: 0.75 }}>
          <Box component="span" sx={{ fontWeight: 600, color: ink.muted }}>Also on the table: </Box>
          {meal.fixedItems.join(' · ')}
        </Typography>
      )}
      {meal.note && (
        <Typography sx={{ color: ink.muted, fontSize: 12.5, lineHeight: 1.55, mt: 0.75, fontStyle: 'italic' }}>
          {meal.note}
        </Typography>
      )}

      {meal.groups.length === 0 ? (
        <Typography sx={{ color: ink.muted, fontSize: 13, mt: 1 }}>
          Nothing to choose here — this one comes as it is.
        </Typography>
      ) : (
        meal.groups.map((g) => (
          <Box key={g.key} sx={{ mt: 2 }}>
            <Typography
              id={`label-${g.key}`}
              sx={{ fontWeight: 700, color: ink.primary, fontSize: 13.5, mb: 1 }}
            >
              {g.group.label}
            </Typography>
            {g.group.helpText && (
              <Typography sx={{ color: ink.secondary, fontSize: 12.5, mb: 1, mt: -0.5 }}>
                {g.group.helpText}
              </Typography>
            )}
            <Box
              role="radiogroup"
              aria-labelledby={`label-${g.key}`}
              sx={{
                display: 'grid',
                // one column on a phone, two the moment there is room
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(230px, 1fr))' },
                gap: 1,
              }}
            >
              {g.options.map((o) => (
                <OptionCard
                  key={o.id}
                  option={o}
                  selected={choices[g.key] === o.id}
                  onSelect={() => onChoose(g, o.id)}
                />
              ))}
            </Box>
          </Box>
        ))
      )}
    </Paper>
  );
};

const MealChoicePicker = ({
  plan = [],
  choices = {},
  onChoose,
  surpriseUs = false,
  onSurpriseUs,
  cutoffDays,
}) => {
  const dayRefs = useRef({});
  const answerable = useMemo(() => plan.filter((d) => d.questionCount > 0), [plan]);

  const [openDay, setOpenDay] = useState(() => {
    const first = answerable.find((d) => dayProgress(d, choices).answered < d.questionCount);
    return (first || answerable[0])?.stayDayIndex ?? null;
  });

  const totals = useMemo(() => {
    const groups = plan.flatMap((d) => d.meals.flatMap((m) => m.groups));
    return { total: groups.length, answered: groups.filter((g) => choices[g.key]).length };
  }, [plan, choices]);

  const jumpTo = useCallback((stayDayIndex) => {
    setOpenDay(stayDayIndex);
    // let the collapse settle before scrolling, or we scroll to where the
    // header was rather than where it lands
    window.setTimeout(() => {
      dayRefs.current[stayDayIndex]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 260);
  }, []);

  /**
   * Answering the last question of a day opens the next unfinished one.
   * Guarded on the day not already being complete, so going back to change a
   * dish does not throw you forward again.
   */
  const handleChoose = useCallback((g, optionId) => {
    onChoose(g.key, optionId);

    const day = plan.find((d) => d.stayDayIndex === g.stayDayIndex);
    if (!day) return;
    const groups = day.meals.flatMap((m) => m.groups);
    const wasComplete = groups.every((x) => choices[x.key]);
    const nowComplete = groups.every((x) => (x.key === g.key ? optionId : choices[x.key]));
    if (wasComplete || !nowComplete) return;

    const after = { ...choices, [g.key]: optionId };
    const next = answerable.find(
      (d) => d.stayDayIndex > day.stayDayIndex && dayProgress(d, after).answered < d.questionCount,
    );
    if (next) jumpTo(next.stayDayIndex);
  }, [plan, answerable, choices, onChoose, jumpTo]);

  const percent = totals.total ? (totals.answered / totals.total) * 100 : 100;

  return (
    <Stack spacing={2.5}>
      {/* One switch for the entire stay. It used to be an option inside every
          group, so a three-night stay asked fourteen separate times. */}
      <Paper
        elevation={0}
        sx={{
          p: 2, borderRadius: 2.5,
          border: `1.5px solid ${surpriseUs ? brand.mustardInk : line.default}`,
          bgcolor: surpriseUs ? alpha(brand.mustard, 0.12) : surface.card,
        }}
      >
        <FormControlLabel
          sx={{ m: 0, alignItems: 'flex-start', width: '100%' }}
          control={<Switch checked={surpriseUs} onChange={(e) => onSurpriseUs(e.target.checked)} sx={{ mr: 1.5 }} />}
          label={(
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <AutoAwesomeRoundedIcon sx={{ fontSize: 17, color: brand.mustardInk }} />
                <Typography sx={{ fontWeight: 700, color: ink.primary }}>Surprise us</Typography>
              </Stack>
              <Typography sx={{ color: ink.secondary, fontSize: 13.5, lineHeight: 1.6, mt: 0.25 }}>
                The kitchen cooks whatever is best that day. Turn this on and there is
                nothing else to pick.
              </Typography>
            </Box>
          )}
        />
      </Paper>

      <Collapse in={!surpriseUs} unmountOnExit>
        <Stack spacing={2}>
          <Box>
            <Typography sx={{ color: ink.secondary, fontSize: 14, lineHeight: 1.65 }}>
              We are a farm kitchen, not a restaurant — everything is cooked fresh for you,
              so we need your choices in advance. One choice per course, for the whole table.
            </Typography>
            {cutoffDays != null && (
              <Typography sx={{ color: ink.muted, fontSize: 13, mt: 0.75 }}>
                You can still change these up to {cutoffDays} days before you arrive.
              </Typography>
            )}
          </Box>

          {/* Always-visible progress. A count is the difference between "this is
              long" and "this is nearly done". */}
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <RestaurantMenuRoundedIcon sx={{ fontSize: 17, color: ink.muted }} />
                <Typography sx={{ fontWeight: 700, color: ink.primary, fontSize: 13.5 }}>
                  {totals.answered} of {totals.total} chosen
                </Typography>
              </Stack>
              {totals.answered === totals.total && totals.total > 0 && (
                <Typography sx={{ color: brand.oliveInk, fontWeight: 700, fontSize: 13 }}>
                  All done
                </Typography>
              )}
            </Stack>
            <LinearProgress
              variant="determinate"
              value={percent}
              aria-label={`${totals.answered} of ${totals.total} choices made`}
              sx={{
                height: 6, borderRadius: 3, bgcolor: surface.sunken,
                '& .MuiLinearProgress-bar': {
                  bgcolor: totals.answered === totals.total ? brand.oliveInk : brand.mustardInk,
                  borderRadius: 3,
                },
              }}
            />
          </Box>

          {/* The day rail. Scrolls sideways rather than wrapping, so a week-long
              stay does not push the first meal below the fold. */}
          {answerable.length > 1 && (
            <Box
              sx={{
                display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5,
                mx: { xs: -2.5, md: 0 }, px: { xs: 2.5, md: 0 },
                scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {answerable.map((day) => {
                const { answered, total } = dayProgress(day, choices);
                const done = total > 0 && answered === total;
                const active = openDay === day.stayDayIndex;
                return (
                  <Chip
                    key={day.stayDayIndex}
                    onClick={() => jumpTo(day.stayDayIndex)}
                    icon={done ? <CheckRoundedIcon sx={{ fontSize: 15 }} /> : undefined}
                    label={`${DAY_RAIL_LABEL(day)}${done ? '' : ` · ${answered}/${total}`}`}
                    sx={{
                      flexShrink: 0, height: 34, fontWeight: 700, fontSize: 13,
                      border: '1.5px solid',
                      borderColor: active ? brand.brown : done ? alpha(brand.oliveInk, 0.4) : line.default,
                      bgcolor: active ? brand.brown : surface.card,
                      color: active ? ink.onDark : done ? brand.oliveInk : ink.primary,
                      '& .MuiChip-icon': { color: active ? ink.onDark : brand.oliveInk },
                      '&:hover': { bgcolor: active ? brand.brown : surface.sunken },
                    }}
                  />
                );
              })}
            </Box>
          )}

          {plan.map((day) => {
            const { answered, total } = dayProgress(day, choices);
            const done = total > 0 && answered === total;
            const open = openDay === day.stayDayIndex;
            const empty = total === 0;

            return (
              <Box
                key={day.stayDayIndex}
                ref={(el) => { dayRefs.current[day.stayDayIndex] = el; }}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${open ? line.default : line.subtle}`,
                  bgcolor: open ? surface.page : surface.card,
                  overflow: 'hidden',
                  // clears the fixed header when we scroll a day into view
                  scrollMarginTop: { xs: 72, sm: 84 },
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => (empty ? null : setOpenDay(open ? null : day.stayDayIndex))}
                  aria-expanded={open}
                  disabled={empty}
                  sx={{
                    width: '100%', font: 'inherit', textAlign: 'left', border: 0, bgcolor: 'transparent',
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: { xs: 1.75, sm: 2.25 }, py: 1.75, minHeight: 60,
                    cursor: empty ? 'default' : 'pointer',
                    '&:focus-visible': { outline: `2px solid ${brand.mustardInk}`, outlineOffset: -2 },
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      flexShrink: 0, width: 26, height: 26, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12.5, fontWeight: 700,
                      bgcolor: done ? brand.oliveInk : open ? brand.brown : surface.sunken,
                      color: done || open ? ink.onDark : ink.secondary,
                    }}
                  >
                    {done ? <CheckRoundedIcon sx={{ fontSize: 16 }} /> : day.stayDayIndex}
                  </Box>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography sx={{ fontWeight: 700, color: ink.primary, fontSize: 15, lineHeight: 1.3 }}>
                      {day.dayLabel}
                    </Typography>
                    <Typography sx={{ color: ink.secondary, fontSize: 12.5, mt: 0.15 }}>
                      {empty
                        ? 'Nothing to choose on this day'
                        : done
                          ? `All ${total} chosen`
                          : `${answered} of ${total} chosen`}
                    </Typography>
                  </Box>
                  {!empty && (
                    <IconButton component="span" size="small" aria-hidden tabIndex={-1} sx={{ pointerEvents: 'none' }}>
                      <ExpandMoreRoundedIcon
                        sx={{
                          color: ink.muted,
                          transform: open ? 'rotate(180deg)' : 'none',
                          transition: 'transform 200ms ease',
                        }}
                      />
                    </IconButton>
                  )}
                </Box>

                <Collapse in={open && !empty} unmountOnExit>
                  <Stack spacing={1.5} sx={{ px: { xs: 1.25, sm: 1.75 }, pb: 2 }}>
                    {day.meals.map((meal) => (
                      <MealCard key={meal.id} meal={meal} choices={choices} onChoose={handleChoose} />
                    ))}
                  </Stack>
                </Collapse>
              </Box>
            );
          })}
        </Stack>
      </Collapse>
    </Stack>
  );
};

export default MealChoicePicker;
